/**
 * The sale-history window — the pure rules behind `salesLoadedFrom`.
 *
 * Since 089 (2026-09-15) a device does not load every sale the shop has
 * ever written. It loads the last SALES_WINDOW_DAYS at start and extends
 * BACKWARDS on demand, always as one contiguous range that ends now:
 *
 *     [salesLoadedFrom ............................ now]
 *
 * That shape is the invariant every consumer leans on. A return is newer
 * than the sale it reverses, so if a sale is loaded, every return of it is
 * loaded too — the return dialog's „already returned" count stays right
 * without a second query. `ensureCustomerSales` is the one deliberate
 * exception (a customer's rows outside the range, fetched by customer), and
 * it is safe for the same reason: 087 stamps a return with the original's
 * customer, so the customer query brings the returns along.
 *
 * `salesLoadedFrom === null` means the range has no left edge: the whole
 * history is in memory, and the notices that say „ბოლო 90 დღე" disappear.
 */

import { businessDay, businessDayRangeUtc } from './business-day'

export const SALES_WINDOW_DAYS = 90
const DAY = 86_400_000

/** The left edge of the boot window: now minus the window, as an ISO string
 *  the filters can compare with `created_at` (timestamptz, UTC). */
export function windowStart(now: number = Date.now()): string {
  return new Date(now - SALES_WINDOW_DAYS * DAY).toISOString()
}

/**
 * The edge right after hydrate. A shop whose first sale is inside the boot
 * window has its WHOLE history in memory already — the range has no left
 * edge, no notice shows and no screen ever fetches. Today (2026-09-15) that
 * is every shop on the platform; the window starts to matter in December.
 * Unknown first sale (the probe failed): keep the edge, stay honest.
 */
export function bootEdge(salesFrom: string, firstAt: string | null | undefined): string | null {
  if (firstAt === undefined) return salesFrom
  if (firstAt === null) return null
  return Date.parse(firstAt) < Date.parse(salesFrom) ? salesFrom : null
}

/** One more window back from the current left edge. */
export function olderWindow(loadedFrom: string): string {
  return new Date(Date.parse(loadedFrom) - SALES_WINDOW_DAYS * DAY).toISOString()
}

/**
 * Does a screen that wants history back to `wanted` (null = all of it)
 * need a fetch, given what is loaded from `loadedFrom` (null = all)?
 * Compared as instants, not strings — the two may carry different suffixes
 * (`Z` from toISOString, `+00:00` from PostgREST).
 */
export function needsOlder(loadedFrom: string | null, wanted: string | null): boolean {
  if (loadedFrom === null) return false
  if (wanted === null) return true
  return Date.parse(wanted) < Date.parse(loadedFrom)
}

/**
 * A request that reaches past the shop's first sale is a request for
 * everything: answer it as `null` so the range closes and the notices go.
 * `firstAt` undefined = not known (the probe failed) — then the request
 * stands as asked and the notice stays, which is the honest default.
 */
export function clampToFirst(wanted: string | null, firstAt: string | null | undefined): string | null {
  if (wanted === null) return null
  if (firstAt === undefined) return wanted
  if (firstAt === null) return null // a shop with no sales has nothing older
  return Date.parse(wanted) <= Date.parse(firstAt) ? null : wanted
}

/** Is there anything older than the loaded range? (false when unknown-empty) */
export function hasOlder(loadedFrom: string | null, firstAt: string | null | undefined): boolean {
  if (loadedFrom === null) return false
  if (firstAt === undefined) return true // unknown — offer the button, the fetch will tell
  if (firstAt === null) return false
  return Date.parse(firstAt) < Date.parse(loadedFrom)
}

/** Union by id; a row that arrives twice keeps the LATER copy (server truth).
 *  1.26 — indexes only the few incoming rows: the old version put every
 *  existing row in a Map too, and a live sale on a 45 000-sale shop merged
 *  140 000 lines that way (~0.4 s on a CPU×4 phone, on every signal). The
 *  store's lists never hold one id twice (they are built by these merges),
 *  which is what lets `existing` be walked instead of indexed. */
export function mergeById<T extends { id: string }>(existing: T[], incoming: T[]): T[] {
  if (incoming.length === 0) return existing
  const inc = new Map<string, T>()
  for (const r of incoming) inc.set(r.id, r)
  const out = existing.map((r) => {
    const fresh = inc.get(r.id)
    if (fresh === undefined) return r
    inc.delete(r.id)
    return fresh
  })
  for (const r of inc.values()) out.push(r)
  return out
}

/** Rows this device adds itself (a sale, a return) go first — and never
 *  twice: a live refresh can bring the same sale a moment before the till's
 *  own RPC answer does (Codex's 1.26 review). Before 1.26 the next merge's Map
 *  happened to collapse such a pair; `mergeById` now relies on the lists
 *  holding each id once, so the adders keep that true. */
export function prependUnique<T extends { id: string }>(rows: T[], list: T[]): T[] {
  const ids = new Set(rows.map((r) => r.id))
  return [...rows, ...list.filter((r) => !ids.has(r.id))]
}

/** The same at the end — a sale's lines. */
export function appendUnique<T extends { id: string }>(list: T[], rows: T[]): T[] {
  const ids = new Set(rows.map((r) => r.id))
  return [...list.filter((r) => !ids.has(r.id)), ...rows]
}

/** Newest first, id as the tiebreaker — the order hydrate has always used,
 *  and the one the history list, the credit ledger and the pulse rely on. */
export function sortSalesDesc<T extends { id: string; created_at: string }>(rows: T[]): T[] {
  // 1.26 — each date parsed once, not twice per comparison (~1.4 million
  // parses for 45 000 sales); the same comparator, so the same order.
  const keyed = rows.map((r) => ({ r, t: Date.parse(r.created_at) }))
  keyed.sort((a, b) => {
    const d = b.t - a.t
    return d !== 0 ? d : (a.r.id < b.r.id ? -1 : a.r.id > b.r.id ? 1 : 0)
  })
  return keyed.map((k) => k.r)
}

/**
 * 1.24 — the boot's RECENT range: what the till needs before it opens.
 * The earlier of today's business-day start and the earliest open shift
 * (so every open shift's sales are in — a close's expected cash), never
 * before the 90-day window (a shift forgotten open for months must not turn
 * phase 1 into the whole history). Phase 2 loads [windowStart, this).
 */
export function bootRecentFrom(o: { now: number; dayStartHour: number; earliestOpenShift: string | null; windowStartIso: string }): string {
  const dayFrom = Date.parse(businessDayRangeUtc(businessDay(o.now, o.dayStartHour), o.dayStartHour).from)
  const open = o.earliestOpenShift ? Date.parse(o.earliestOpenShift) : Infinity
  const from = Math.max(Math.min(dayFrom, open), Date.parse(o.windowStartIso))
  return new Date(from).toISOString()
}

/** Union by id where the row ALREADY HERE wins — phase 2's older copy must
 *  never overwrite one realtime, refreshLive or a fresh sale put in. */
export function mergeKeepExisting<T extends { id: string }>(existing: T[], incoming: T[]): T[] {
  if (incoming.length === 0) return existing
  const m = new Map<string, T>()
  for (const r of incoming) if (!m.has(r.id)) m.set(r.id, r)
  for (const r of existing) m.set(r.id, r)
  return [...m.values()]
}

/** Phase 2 lands only in the hydrate that started it, on the edge it read up to. */
export function bootHistoryCommits(o: { gen: number; currentGen: number; edge: string | null; currentEdge: string | null }): boolean {
  return o.gen === o.currentGen && o.edge === o.currentEdge
}

/** 1.24 (Codex's review) — what „retry" does: nothing while the history is
 *  in or still loading; phase 2 again when phase 2 failed; the whole hydrate
 *  when the boot itself failed and left no phase 2 to retry. */
export function historyRetryAction(s: { historyReady: boolean; historyFailed: boolean; phase2Pending: boolean }): 'none' | 'phase2' | 'hydrate' {
  if (s.historyReady || !s.historyFailed) return 'none'
  return s.phase2Pending ? 'phase2' : 'hydrate'
}
